import { useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, Timestamp, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  Box,
  Card,
  Stack,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getLeagueForTrophies } from '../config/arenaLeagues';
import { v4 as uuid } from 'uuid';
import backgroundImage from '../data/LoginBackgroundImage.png';

// ---- Beasts of Legend palette & type ----
const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';

// Starter card IDs by faction
const STARTER_CARD_BY_FACTION = {
  Man: 'squire',
  Beast: 'hound',
};

// Shared TextField styles so all inputs match the dark / gold theme
const textFieldStyles = {
  mt: 1,
  '& .MuiInputLabel-root': {
    color: 'rgba(249,246,227,0.75)',
    fontFamily: `'Cinzel', serif`,
    letterSpacing: '0.4px',
    '&.Mui-focused': {
      color: GOLD,
      textShadow: '0 0 8px rgba(212,175,55,0.4)',
    },
  },
  '& .MuiOutlinedInput-root': {
    color: TEXT_WARM,
    backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: 1.5,
    transition:
      'background-color 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
    '& fieldset': {
      borderColor: GOLD_DIM,
    },
    '&:hover fieldset': {
      borderColor: GOLD,
      boxShadow: '0 0 10px rgba(212,175,55,0.3)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(0,0,0,0.92)',
    },
    '&.Mui-focused fieldset': {
      borderColor: GOLD,
      boxShadow:
        '0 0 0 1px rgba(212,175,55,0.7), 0 0 18px rgba(212,175,55,0.45)',
    },
  },
  '& .MuiInputBase-input': {
    color: TEXT_WARM,
    paddingY: 1.3,
  },
};

function Login() {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState(''); // for email/password register
  const [password, setPassword] = useState('');
  const [faction, setFaction] = useState('Man'); // for email/password register

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  // Shared starter dialog state (for guest + first-time Google + "incomplete profile")
  const [starterDialogOpen, setStarterDialogOpen] = useState(false);
  const [starterMode, setStarterMode] = useState(null); // 'guest' | 'google' | 'existing'
  const [starterFaction, setStarterFaction] = useState('Man');
  const [starterUsername, setStarterUsername] = useState('');
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  const navigate = useNavigate();

  const isValidFaction = (val) => !!val && !!STARTER_CARD_BY_FACTION[val];

  const isOnboardingComplete = (data) => {
    const hasUsername = typeof data?.username === 'string' && data.username.trim().length > 0;
    const hasFaction = typeof data?.faction === 'string' && isValidFaction(data.faction);
    const hasCards = Array.isArray(data?.ownedCards) && data.ownedCards.length > 0;
    return hasUsername && hasFaction && hasCards;
  };

  // Create or patch user doc with starter card + faction when appropriate
  const createStarterDocForUser = async (user, chosenFaction, options = {}) => {
    const { isGuest = false, usernameOverride } = options;
    const starterCardId = STARTER_CARD_BY_FACTION[chosenFaction];

    const starterCardInstance = starterCardId
      ? {
          instanceId: uuid(),
          cardId: starterCardId,
          level: 1,
          xp: 0,
          evolutionStage: 1,
          active: true,
          defenseActive: true,
        }
      : null;

    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    const existing = snap.exists() ? snap.data() || {} : {};

    const hasOwnedCards =
      Array.isArray(existing.ownedCards) && existing.ownedCards.length > 0;

    const trophiesSafe =
      typeof existing.trophies === 'number' ? existing.trophies : 0;

    const payload = {
      username:
        (usernameOverride && usernameOverride.trim()) ||
        existing.username ||
        user.displayName ||
        (isGuest ? `Guest-${(user.uid || '').slice(-4)}` : 'Commander'),
      email:
        existing.email !== undefined
          ? existing.email
          : user.email ?? null,
      faction: chosenFaction || existing.faction || null,
      coins: typeof existing.coins === 'number' ? existing.coins : 0,
      consumables:
        existing.consumables || { soulDust: 0, arenaTicket: 0 },
      drawTickets: existing.drawTickets || {
        basic: 20,
        rarePlus: 0,
        highRarePlus: 0,
        superRarePlus: 0,
      },
      trophies: trophiesSafe,
      league: existing.league || getLeagueForTrophies(trophiesSafe).name,
      campaign: existing.campaign || {  world: 1, stage: 1 },
      privacy:
        existing.privacy || {
          allowTradeBrowse: true,
        },
      createdAt: existing.createdAt || new Date(),
      lastLoginAt: new Date(),
      arenaRefreshAt: existing.arenaRefreshAt || Timestamp.now(),
      arenaBeatenOpponents: existing.arenaBeatenOpponents || [],
    };

    // Only grant starter card if user has no cards yet
    if (!hasOwnedCards && starterCardInstance) {
      payload.ownedCards = [starterCardInstance];
    }

    await setDoc(userRef, payload, { merge: true });
  };

  /**
   * IMPORTANT FIX:
   * If a user is already authenticated (Google sign-in completes instantly),
   * they can get redirected before they finish onboarding.
   *
   * So we always check Firestore for completeness:
   * - If missing username/faction/cards, force the starter dialog.
   * - Only navigate when complete.
   */
  const checkAndForceOnboardingIfNeeded = async (user) => {
    if (!user?.uid) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() || {} : {};

      if (isOnboardingComplete(data)) {
        // All good
        return { needsOnboarding: false, data };
      }

      // Needs onboarding: open dialog and hold user here until confirmed.
      setPendingGoogleUser(user);
      setStarterMode('existing'); // authenticated but incomplete profile
      setStarterFaction(isValidFaction(data.faction) ? data.faction : 'Man');
      setStarterUsername(
        (data.username && data.username.trim()) ||
          user.displayName ||
          ''
      );
      setStarterDialogOpen(true);

      return { needsOnboarding: true, data };
    } catch (err) {
      console.error(err);
      // If Firestore read fails, don't hard-lock user; just show a helpful error.
      setError(err?.message || 'Failed to load your profile. Please try again.');
      return { needsOnboarding: false, data: null };
    }
  };

  // If the user refreshes after Google auth, or your app auto-redirects back here,
  // this makes sure they still get the username/faction + starter card prompt.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    // Only run if the dialog isn't already open (avoid loops)
    if (starterDialogOpen) return;
    checkAndForceOnboardingIfNeeded(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = async () => {
    setError('');
    setMessage('');
    setIsBusy(true);
    try {
      if (isRegistering) {
        if (!email || !password || !username) {
          setError('Please enter email, username, and password.');
          return;
        }
        if (!isValidFaction(faction)) {
          setError('Please choose a valid faction.');
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;

        await createStarterDocForUser(user, faction, {
          isGuest: false,
          usernameOverride: username,
        });

        navigate('/');
      } else {
        await signInWithEmailAndPassword(auth, email, password);

        // Optional: if someone signed in and their doc is incomplete, force onboarding
        const user = auth.currentUser;
        if (user) {
          const res = await checkAndForceOnboardingIfNeeded(user);
          if (!res?.needsOnboarding) navigate('/');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setMessage('');
    if (!email) {
      setError('Enter your email address to receive a password reset link.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      setError(err.message);
    }
  };

  // ----- Guest flow -----

  const handleOpenGuestStarterDialog = () => {
    setError('');
    setMessage('');
    setStarterMode('guest');
    setStarterFaction('Man');
    setStarterUsername('');
    setPendingGoogleUser(null);
    setStarterDialogOpen(true);
  };

  // ----- Google flow -----

  const handleGoogleLogin = async () => {
    setError('');
    setMessage('');
    setIsBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Always check completeness (username + faction + starter card).
      // Even if a doc exists, it might be missing fields/cards.
      const res = await checkAndForceOnboardingIfNeeded(user);

      if (!res?.needsOnboarding) {
        // Returning player OR already complete
        navigate('/');
      } else {
        // Dialog opened by checkAndForceOnboardingIfNeeded
        setStarterMode('google');
      }
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user') return;
      console.error(err);
      setError(err?.message || 'Failed to sign in with Google.');
    } finally {
      setIsBusy(false);
    }
  };

  // ----- Shared starter dialog handlers (guest + google + existing incomplete) -----

  const handleCloseStarterDialog = () => {
    setStarterDialogOpen(false);
    setStarterMode(null);
    setPendingGoogleUser(null);
    setError('');
    setMessage('');
  };

  const handleConfirmStarter = async () => {
    setError('');
    setMessage('');
    const trimmedUsername = starterUsername.trim();

    if (!trimmedUsername) {
      setError('Please choose a username.');
      return;
    }

    if (!isValidFaction(starterFaction)) {
      setError('Please choose a valid faction.');
      return;
    }

    setIsBusy(true);
    try {
      if (starterMode === 'guest') {
        // Sign in anonymously NOW, using chosen username + faction
        const userCredential = await signInAnonymously(auth);
        const user = userCredential.user;

        await createStarterDocForUser(user, starterFaction, {
          isGuest: true,
          usernameOverride: trimmedUsername,
        });

        handleCloseStarterDialog();
        navigate('/');
      } else {
        // google / existing (already authenticated)
        const user = pendingGoogleUser || auth.currentUser;
        if (!user) {
          setError('Your session expired. Please sign in again.');
          return;
        }

        await createStarterDocForUser(user, starterFaction, {
          isGuest: false,
          usernameOverride: trimmedUsername,
        });

        handleCloseStarterDialog();
        navigate('/');
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to create starter account.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Box
      className="login-page"
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-screen background layer */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 520,
          px: { xs: 3, sm: 4 },
          py: { xs: 3.5, sm: 4.5 },
          bgcolor: 'rgba(0,0,0,0.75)',
          borderRadius: 2,
          border: `1px solid ${GOLD_DIM}`,
          boxShadow: `0 0 32px rgba(212,175,55,0.25)`,
          backdropFilter: 'blur(6px)',
        }}
      >
        <Stack spacing={2.75}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: `'Cinzel', serif`,
              fontWeight: 700,
              letterSpacing: '0.9px',
              color: GOLD,
              textAlign: 'center',
              textShadow: '0 0 14px rgba(212,175,55,0.32)',
            }}
          >
            {isRegistering ? 'Create Account' : 'Return to the Arena'}
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              textAlign: 'center',
              color: 'rgba(249,246,227,0.72)',
              mb: 1,
            }}
          >
            {isRegistering
              ? 'Choose your faction and claim your first card.'
              : 'Log in to command your cards and climb the leagues.'}
          </Typography>

          <TextField
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={textFieldStyles}
          />

          {isRegistering && (
            <>
              <TextField
                label="Username"
                fullWidth
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={textFieldStyles}
              />

              <FormControl
                component="fieldset"
                sx={{
                  mt: 2,
                  mb: 1,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: `1px solid ${GOLD_DIM}`,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  boxShadow: '0 0 14px rgba(212,175,55,0.18)',
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{
                    color: GOLD_SOFT,
                    fontFamily: `'Cinzel', serif`,
                    letterSpacing: '0.6px',
                    mb: 0.75,
                    '&.Mui-focused': { color: GOLD },
                  }}
                >
                  Choose your faction
                </FormLabel>
                <RadioGroup
                  row
                  name="faction"
                  value={faction}
                  onChange={(e) => setFaction(e.target.value)}
                >
                  <FormControlLabel
                    value="Man"
                    control={
                      <Radio
                        sx={{
                          color: GOLD_SOFT,
                          '&.Mui-checked': { color: GOLD },
                        }}
                      />
                    }
                    label="Man"
                    sx={{ color: TEXT_WARM }}
                  />
                  <FormControlLabel
                    value="Beast"
                    control={
                      <Radio
                        sx={{
                          color: GOLD_SOFT,
                          '&.Mui-checked': { color: GOLD },
                        }}
                      />
                    }
                    label="Beast"
                    sx={{ color: TEXT_WARM }}
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}

          <TextField
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={textFieldStyles}
          />

          {!isRegistering && (
            <Button
              onClick={handleForgotPassword}
              variant="text"
              sx={{
                alignSelf: 'flex-end',
                mt: 0.25,
                mb: 1,
                color: GOLD_SOFT,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                letterSpacing: '0.4px',
                '&:hover': {
                  color: GOLD,
                  textShadow: '0 0 10px rgba(212,175,55,0.35)',
                  backgroundColor: 'transparent',
                },
              }}
              disabled={isBusy}
            >
              Forgot Password?
            </Button>
          )}

          {error && (
            <Typography
              sx={{
                color: '#ffb4a9',
                fontSize: 13,
                mt: 0.5,
              }}
            >
              {error}
            </Typography>
          )}
          {message && (
            <Typography
              sx={{
                color: GOLD_SOFT,
                fontSize: 13,
                mt: 0.5,
              }}
            >
              {message}
            </Typography>
          )}

          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {/* Primary email/password auth */}
            <Button
              variant="contained"
              onClick={handleAuth}
              disabled={isBusy}
              sx={{
                py: 1.2,
                borderRadius: 2,
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'none',
                color: '#0b0b0b',
                backgroundImage: `linear-gradient(180deg, ${GOLD}, #b68c1e)`,
                boxShadow: `0 8px 24px rgba(212,175,55,0.35)`,
                '&:hover': {
                  backgroundImage: `linear-gradient(180deg, ${GOLD}, #a37c1a)`,
                  boxShadow: `0 10px 28px rgba(212,175,55,0.45)`,
                },
              }}
            >
              {isRegistering ? 'Create Account' : 'Login'}
            </Button>

            <Button
              onClick={() => setIsRegistering(!isRegistering)}
              variant="text"
              disabled={isBusy}
              sx={{
                alignSelf: 'center',
                color: GOLD_SOFT,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.5px',
                '&:hover': {
                  color: GOLD,
                  textShadow: '0 0 10px rgba(212,175,55,0.35)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              {isRegistering ? 'Already have an account? Log in' : 'No account? Register'}
            </Button>

            {/* --- Divider: other sign-in options --- */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 1,
                mb: 0.5,
              }}
            >
              <Divider
                sx={{
                  flex: 1,
                  borderColor: 'rgba(212,175,55,0.4)',
                }}
              />
              <Typography
                sx={{
                  mx: 1.5,
                  fontSize: 12,
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase',
                  color: 'rgba(249,246,227,0.7)',
                  fontFamily: `'Cinzel', serif`,
                }}
              >
                Or continue with
              </Typography>
              <Divider
                sx={{
                  flex: 1,
                  borderColor: 'rgba(212,175,55,0.4)',
                }}
              />
            </Box>

            {/* Google sign-in */}
            <Button
              onClick={handleGoogleLogin}
              variant="contained"
              disabled={isBusy}
              sx={{
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.5px',
                backgroundColor: '#0b0b0b',
                border: `1px solid ${GOLD_DIM}`,
                color: TEXT_WARM,
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                boxShadow: '0 0 16px rgba(0,0,0,0.8)',
                '&:hover': {
                  backgroundColor: '#151515',
                  boxShadow: '0 0 18px rgba(212,175,55,0.35)',
                },
              }}
            >
              Continue with Google
            </Button>

            {/* Guest sign-in */}
            <Button
              onClick={handleOpenGuestStarterDialog}
              variant="outlined"
              disabled={isBusy}
              sx={{
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.5px',
                color: GOLD_SOFT,
                borderColor: GOLD_DIM,
                background:
                  'linear-gradient(135deg, rgba(0,0,0,0.9), rgba(212,175,55,0.12))',
                boxShadow: '0 0 16px rgba(212,175,55,0.25)',
                '&:hover': {
                  borderColor: GOLD,
                  color: GOLD,
                  boxShadow: '0 0 20px rgba(212,175,55,0.45)',
                  background:
                    'linear-gradient(135deg, rgba(0,0,0,0.95), rgba(212,175,55,0.2))',
                },
              }}
            >
              Continue as Guest
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* Starter dialog (username + faction) for guest + first-time Google + incomplete auth user */}
      <Dialog
        open={starterDialogOpen}
        onClose={handleCloseStarterDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
            border: '1px solid rgba(212,175,55,0.7)',
            boxShadow: '0 0 28px rgba(212,175,55,0.6)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: `'Cinzel', serif`,
            letterSpacing: '0.8px',
            fontWeight: 700,
            color: TEXT_WARM,
            textShadow: '0 0 12px rgba(212,175,55,0.6)',
            pb: 1,
          }}
        >
          {starterMode === 'guest'
            ? 'Continue as Guest'
            : 'Create Your Commander'}
        </DialogTitle>
        <DialogContent sx={{ color: TEXT_WARM, pt: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: 'rgba(249,246,227,0.8)',
            }}
          >
            Choose a commander name and a faction to receive your starter card.
            You can link this account later in Settings to protect your progress.
          </Typography>

          <TextField
            label="Username"
            fullWidth
            variant="outlined"
            value={starterUsername}
            onChange={(e) => setStarterUsername(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiInputLabel-root': {
                color: 'rgba(249,246,227,0.75)',
                fontFamily: `'Cinzel', serif`,
                '&.Mui-focused': {
                  color: GOLD,
                },
              },
              '& .MuiOutlinedInput-root': {
                color: TEXT_WARM,
                backgroundColor: 'rgba(0,0,0,0.8)',
                borderRadius: 1.5,
                '& fieldset': {
                  borderColor: 'rgba(212,175,55,0.4)',
                },
                '&:hover fieldset': {
                  borderColor: GOLD,
                },
                '&.Mui-focused fieldset': {
                  borderColor: GOLD,
                  boxShadow:
                    '0 0 0 1px rgba(212,175,55,0.7), 0 0 18px rgba(212,175,55,0.45)',
                },
              },
            }}
          />

          <FormControl
            component="fieldset"
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: 1.5,
              border: `1px solid ${GOLD_DIM}`,
              backgroundColor: 'rgba(0,0,0,0.5)',
              boxShadow: '0 0 14px rgba(212,175,55,0.18)',
            }}
          >
            <FormLabel
              component="legend"
              sx={{
                color: GOLD_SOFT,
                fontFamily: `'Cinzel', serif`,
                letterSpacing: '0.6px',
                mb: 0.75,
                '&.Mui-focused': { color: GOLD },
              }}
            >
              Choose your faction
            </FormLabel>
            <RadioGroup
              row
              name="starter-faction"
              value={starterFaction}
              onChange={(e) => setStarterFaction(e.target.value)}
            >
              <FormControlLabel
                value="Man"
                control={
                  <Radio
                    sx={{
                      color: GOLD_SOFT,
                      '&.Mui-checked': { color: GOLD },
                    }}
                  />
                }
                label="Man"
                sx={{ color: TEXT_WARM }}
              />
              <FormControlLabel
                value="Beast"
                control={
                  <Radio
                    sx={{
                      color: GOLD_SOFT,
                      '&.Mui-checked': { color: GOLD },
                    }}
                  />
                }
                label="Beast"
                sx={{ color: TEXT_WARM }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 1,
          }}
        >
          <Button
            onClick={handleCloseStarterDialog}
            disabled={isBusy}
            sx={{
              fontFamily: `'Cinzel', serif`,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              color: TEXT_WARM,
              borderRadius: 2,
              border: '1px solid rgba(212,175,55,0.7)',
              px: 2.5,
              py: 0.5,
              background:
                'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(0,0,0,0.9))',
              boxShadow: '0 0 12px rgba(212,175,55,0.35)',
              '&:hover': {
                background:
                  'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(0,0,0,0.95))',
                boxShadow: '0 0 18px rgba(212,175,55,0.6)',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStarter}
            disabled={isBusy}
            sx={{
              ml: 1,
              fontFamily: `'Cinzel', serif`,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              color: '#0b0b0b',
              borderRadius: 2,
              backgroundImage: `linear-gradient(180deg, ${GOLD}, #b68c1e)`,
              boxShadow: '0 8px 24px rgba(212,175,55,0.45)',
              '&:hover': {
                backgroundImage: `linear-gradient(180deg, ${GOLD}, #a37c1a)`,
                boxShadow: '0 10px 30px rgba(212,175,55,0.6)',
              },
            }}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
